import { call, put, takeLatest, delay } from 'redux-saga/effects';
import { AUTH } from 'src/store/ActionTypes';

import * as api from 'src/store/api';

function* logout() {
  try {
    yield call(api.inlineLogout);
    yield put({
      type: AUTH.LOGOUT_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: AUTH.LOGOUT_FAILED,
      payload: 'Unable to logout. Please try again later',
    });
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'logoutStatus',
    });
  }
}

function* login(action) {
  try {
    const { username: usernameOrEmail, password } = action.payload;
    const credentials = yield call(api.inlineLogin, usernameOrEmail, password);
    const { challengeName, session, username, accessToken, refreshToken } = credentials;
    if (challengeName) {
      yield put({
        type: AUTH.PASSWORD_CHALLENGE,
        payload: {
          challengeName,
          session,
          username,
        },
      });
    } else if (accessToken) {
      yield put({
        type: AUTH.LOGIN_SUCCEEDED,
        payload: {
          accessToken,
          refreshToken,
        },
      });
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('username', username);
    } else {
      yield put({
        type: AUTH.LOGIN_FAILED,
        payload: 'Invalid credentials. Please try again.',
      });
    }
  } catch (e) {
    if (e.code === 'PasswordResetRequiredException') {
      // Password Reset is required this is same as forgot password flow
      yield put({
        type: AUTH.PASSWORD_RESET,
        payload: action.payload.username, // eslint-disable-line no-undef
      });
    } else {
      yield put({
        type: AUTH.LOGIN_FAILED,
        payload: e.message,
      });
    }
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'loginStatus',
    });
    yield put({
      type: AUTH.UNSET,
      payload: 'loginError',
    });
    yield put({
      type: AUTH.UNSET,
      payload: 'passwordReset',
    });
  }
}

function* forgotPassword(action) {
  try {
    yield call(api.forgotPassword, action.payload);
    yield put({
      type: AUTH.FORGOT_PASSWORD_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: AUTH.FORGOT_PASSWORD_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'forgotPasswordStatus',
    });
    yield put({
      type: AUTH.UNSET,
      payload: 'forgotPasswordError',
    });
  }
}

function* resendInvitation(action) {
  try {
    yield call(api.resendInvitation, action.payload);
    yield put({
      type: AUTH.RESEND_INVITATION_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: AUTH.RESEND_INVITATION_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'resendInvitationStatus',
    });
  }
}

function* changePassword(action) {
  try {
    const { confirmationCode, username, password } = action.payload;
    yield call(api.changePassword, confirmationCode, username, password);
    yield put({
      type: AUTH.CHANGE_PASSWORD_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: AUTH.CHANGE_PASSWORD_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'changePasswordStatus',
    });
    yield put({
      type: AUTH.UNSET,
      payload: 'changePasswordError',
    });
  }
}

function* passwordChallengeResponse(action) {
  try {
    const { challengeName, session, username, password } = action.payload;
    const credentials = yield call(api.inlineAuthChallenge, challengeName, session, username, password);
    const { accessToken, refreshToken } = credentials;
    yield put({
      type: AUTH.PASSWORD_CHALLENGE_RESPONSE_SUCCEEDED,
      payload: {
        accessToken,
        refreshToken,
      },
    });
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('username', username);
  } catch (e) {
    yield put({
      type: AUTH.PASSWORD_CHALLENGE_RESPONSE_FAILED,
      payload: e.message,
    });
  } finally {
    delay(500);
    yield put({
      type: AUTH.UNSET,
      payload: 'passwordChallengeResponseStatus',
    });
  }
}

function* logoutSaga() {
  yield takeLatest(AUTH.LOGOUT, logout);
}

function* loginSaga() {
  yield takeLatest(AUTH.LOGIN, login);
}

function* passwordChallengeResponseSaga() {
  yield takeLatest(AUTH.PASSWORD_CHALLENGE_RESPONSE, passwordChallengeResponse);
}

function* forgotPasswordSaga() {
  yield takeLatest(AUTH.FORGOT_PASSWORD, forgotPassword);
}

function* resendInvitationSaga() {
  yield takeLatest(AUTH.RESEND_INVITATION, resendInvitation);
}

function* changePasswordSaga() {
  yield takeLatest(AUTH.CHANGE_PASSWORD, changePassword);
}

export default [
  loginSaga(),
  passwordChallengeResponseSaga(),
  resendInvitationSaga(),
  forgotPasswordSaga(),
  changePasswordSaga(),
  logoutSaga(),
];
